import { Features } from '@lessonPlanner/constants';

import { PortalLink } from '../enums/portal.enum';
import { isNonCurriculumSpecific } from '../utils/groupFeatures';
import { addStepNumbersToRoutes } from '../utils/routeStepNumbering';

import {
  LOCAL_CURRICULUM_FEATURES,
  INTERNATIONAL_CURRICULUM_FEATURES,
  USA_CURRICULUM_FEATURES,
  DEFAULT_CURRICULUM_FEATURES,
} from './portalFeatures';

export const routes = {
  LEARNING_COMPETENCY: '/objective',
  LANDING: '/',
  MAIN: '/lesson-plan',
  PAYMENT: '/payment',
  PAYMENT_SUCCESS: '/payment/success',
  PAYMENT_FAIL: '/payment/fail',
  PRICING: '/pricing',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_AND_CONDITIONS: '/terms-of-service-agreement',
  QUIZMAKER: '/quizmaker',
  ACTIVITIES: '/activities',
  SUBJECT_EXTENSIONS: '/cross-subject-extension',
  ANALOGY: '/analogy',
  DAILY_LESSON_LOG: '/daily-lesson-log',
  CATCH_UP_FRIDAYS: '/catch-up-fridays',
  SUMMATIVE_TEST: '/summative-test',
  CEREBRO_LESSON_PLAN: '/cerebro-lesson-plan',
  SCHOOL_CONTACT: '/school-contact',
  V2_LOGIN: '/v2',
  V2_DASHBOARD: '/v2/dashboard',
  V2_LESSON_PLANNER: '/v2/lesson-planner',
  V2_SHORT_QUIZ: '/v2/short-quiz',
  V2_ACTIVITY: '/v2/activity',
  V2_CATCH_UP_FRIDAYS: '/v2/catch-up-fridays',
  V2_HISTORY: '/v2/history',
  V2_OBJECTIVES: '/v2/objectives',
  V2_SUMMATIVE_TEST: '/v2/summative-test',
  DLL: '/dll',
  BLOG: '/blog',
  TOPIC_EXPLORER: '/topic-explorer',
  ENERGIZER: '/energizer',
  ELIF: '/elif',
  PHOTO_BOOTH: '/photo-booth',
  UNIT_PLAN: '/unit-plan',
  PPT_MAKER: '/ppt-maker',
  TUTORIALS: '/tutorials',
  GENERIC_UNPACKING: '/generic-unpacking',
  IPLAN: '/iplan',
  IPLAN_OBJECTIVE: '/iplan-objective',
  MULTIGRADE_LESSON_PLAN: '/multigrade',
};

export const MainRouteLabels = {
  UNIT_PLAN: 'Unit Plan',
  MAIN: 'Lesson Plan',
  DLL: 'DLL',
  SUMMATIVE_TEST: 'Summative Test',
  PPT_MAKER: 'Powerpoint Maker',
  PHOTO_BOOTH: 'Photo Booth',
  TOPIC_EXPLORER: 'Topic Explorer',
  ENERGIZER: 'Energizer',
  ELIF: 'Eli5',
  ACTIVITIES: 'Activity',
  QUIZMAKER: 'Short Quiz',
  CATCH_UP_FRIDAYS: 'Catch-Up Fridays',
  SUBJECT_EXTENSIONS: 'Learning Within and Across the Curriculum',
  LEARNING_COMPETENCY: 'Objectives',
  ANALOGY: 'Analogy',
  GENERIC_UNPACKING: 'Learning Competency',
  IPLAN: 'IPlan',
  IPLAN_OBJECTIVE: 'IPlan Objectives',
  MULTIGRADE_LESSON_PLAN: 'Multi-Grade Lesson Plan',
};

const paymentRoutes = [routes.PAYMENT_SUCCESS, routes.PAYMENT_FAIL];
// Pages that shouldn't trigger country fetching
export const basePublicPages = [
  routes.LANDING,
  routes.PRIVACY_POLICY,
  routes.TERMS_AND_CONDITIONS,
  routes.TUTORIALS,
  ...paymentRoutes,
];
export const publicPages = [...basePublicPages, ...Object.values(PortalLink)];

export const mainRoutes = [
  {
    link: routes.UNIT_PLAN,
    label: MainRouteLabels.UNIT_PLAN,
    featureKey: Features.UNIT_PLAN,
  },
  {
    link: routes.MAIN,
    label: MainRouteLabels.MAIN,
    featureKey: Features.LESSON_PLAN,
  },
  {
    link: routes.DLL,
    label: MainRouteLabels.DLL,
    featureKey: Features.DLL,
  },
  {
    link: routes.SUMMATIVE_TEST,
    label: MainRouteLabels.SUMMATIVE_TEST,
    featureKey: Features.SUMMATIVE_TEST,
  },
  {
    link: routes.PPT_MAKER,
    label: MainRouteLabels.PPT_MAKER,
    featureKey: Features.PPT_MAKER,
    isNewFeature: true,
  },
  {
    link: routes.PHOTO_BOOTH,
    label: MainRouteLabels.PHOTO_BOOTH,
    featureKey: Features.PHOTO_BOOTH,
    isNewFeature: true,
  },
  {
    link: routes.TOPIC_EXPLORER,
    label: MainRouteLabels.TOPIC_EXPLORER,
    featureKey: Features.TOPIC_EXPLORER,
    isNewFeature: true,
  },
  {
    link: routes.ENERGIZER,
    label: MainRouteLabels.ENERGIZER,
    featureKey: Features.ENERGIZER,
  },
  {
    link: routes.ELIF,
    label: MainRouteLabels.ELIF,
    featureKey: Features.ELIF,
  },
  {
    link: routes.ACTIVITIES,
    label: MainRouteLabels.ACTIVITIES,
    featureKey: Features.ACTIVITIES,
  },
  {
    link: routes.QUIZMAKER,
    label: MainRouteLabels.QUIZMAKER,
    featureKey: Features.QUIZMAKER,
  },
  {
    link: routes.CATCH_UP_FRIDAYS,
    label: MainRouteLabels.CATCH_UP_FRIDAYS,
    featureKey: Features.CATCH_UP_FRIDAYS,
  },
  {
    link: routes.SUBJECT_EXTENSIONS,
    label: MainRouteLabels.SUBJECT_EXTENSIONS,
    featureKey: Features.SUBJECT_EXTENSIONS,
  },
  {
    link: routes.LEARNING_COMPETENCY,
    label: MainRouteLabels.LEARNING_COMPETENCY,
    featureKey: Features.OBJECTIVES,
  },
  {
    link: routes.ANALOGY,
    label: MainRouteLabels.ANALOGY,
    featureKey: Features.ANALOGY,
  },
  {
    link: routes.GENERIC_UNPACKING,
    label: MainRouteLabels.GENERIC_UNPACKING,
    featureKey: Features.GENERIC_UNPACKING,
    isNewFeature: true,
  },
  {
    link: routes.IPLAN,
    label: MainRouteLabels.IPLAN,
    featureKey: Features.LESSON_PLAN,
    isNewFeature: true,
  },
  {
    link: routes.IPLAN_OBJECTIVE,
    label: MainRouteLabels.IPLAN_OBJECTIVE,
    featureKey: Features.IPLAN_OBJECTIVES,
    isNewFeature: true,
  },
  {
    link: routes.MULTIGRADE_LESSON_PLAN,
    label: MainRouteLabels.MULTIGRADE_LESSON_PLAN,
    featureKey: Features.MULTIGRADE_LESSON_PLAN,
    isNewFeature: true,
  },
];

export const nonCurriculumRoutes = mainRoutes.filter((route) =>
  isNonCurriculumSpecific(route.featureKey),
);

export const iPlanRoutes = addStepNumbersToRoutes(
  [
    routes.GENERIC_UNPACKING,
    routes.IPLAN_OBJECTIVE,
    routes.MAIN,
    routes.MULTIGRADE_LESSON_PLAN,
  ],
  mainRoutes,
  3,
);

export const localRoutes = LOCAL_CURRICULUM_FEATURES.map((feature) =>
  mainRoutes.find((route) => route.featureKey === feature),
).filter(Boolean);

export const internationalRoutes = INTERNATIONAL_CURRICULUM_FEATURES.map(
  (feature) => mainRoutes.find((route) => route.featureKey === feature),
).filter(Boolean);

export const usaRoutes = USA_CURRICULUM_FEATURES.map((feature) =>
  mainRoutes.find((route) => route.featureKey === feature),
).filter(Boolean);

export const defaultRoutes = DEFAULT_CURRICULUM_FEATURES.map((feature) =>
  mainRoutes.find((route) => route.featureKey === feature),
).filter(Boolean);

export const staticPremiumRoutes = [
  routes.SUMMATIVE_TEST,
  routes.DLL,
  routes.PHOTO_BOOTH,
  routes.PPT_MAKER,
  routes.UNIT_PLAN,
];

export const conditionalPremiumRoutes = [routes.MAIN, routes.TOPIC_EXPLORER];

export const currencyMap = {
  PH: 'PHP',
  US: 'USD',
};
